import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Tv, RefreshCw, Users, Trophy, Swords, Copy } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const MODES = [
  { key: "lobby", label: "Sảnh chờ", desc: "Hiện QR + danh sách check-in", icon: Users },
  { key: "match", label: "Trận đấu", desc: "Tập trung hiện trận đang diễn ra", icon: Swords },
  { key: "leaderboard", label: "Bảng xếp hạng", desc: "Tập trung hiện top 10", icon: Trophy },
];

export default function AdminTVControl() {
  const [session, setSession] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const tvUrl = useMemo(() => {
    const host = window.location.hostname;
    const apex = host.replace(/^(admin|tv|app)\./, "");
    return `${window.location.protocol}//tv.${apex}/tv`;
  }, []);

  const load = async () => {
    const [s, c] = await Promise.all([api.get("/tv/session"), api.get("/tv/checkins")]);
    setSession(s.data);
    setCheckins(c.data);
  };
  useEffect(() => {
    load();
    const t = setInterval(() => api.get("/tv/checkins").then(({ data }) => setCheckins(data)), 5000);
    return () => clearInterval(t);
  }, []);

  const setMode = async (mode) => {
    try {
      const { data } = await api.post("/admin/tv/mode", { mode });
      setSession(data);
      toast.success(`Đã chuyển TV: ${mode}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lỗi");
    }
  };

  const refresh = async () => {
    try {
      const { data } = await api.post("/admin/tv/refresh");
      setSession(data);
      toast.success("Đã làm mới QR token");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lỗi");
    }
  };

  if (!session) return <div className="p-8 text-white/40">Đang tải...</div>;

  const host = window.location.hostname;
  const apex = host.replace(/^(admin|tv|app)\./, "");
  // QR scan opens the player flow on the apex domain (or app. if set)
  const checkinUrl = `${window.location.protocol}//${apex}/tv/checkin?t=${session.qr_token}`;

  return (
    <div className="p-5 sm:p-8 space-y-5 max-w-5xl">
      <div className="flex items-center gap-3 mb-2">
        <Tv className="text-cyan-300" />
        <div>
          <h1 className="font-display font-black text-2xl">Quản lý TV Display</h1>
          <p className="text-xs text-white/50">Điều khiển chế độ TV, làm mới QR sảnh chờ</p>
        </div>
      </div>

      {/* Mode selector */}
      <div className="rounded-2xl bg-[#0c0d14] border border-white/8 p-5">
        <div className="font-display font-bold text-sm uppercase tracking-wider mb-3 text-amber-300">Chế độ hiển thị</div>
        <div className="grid sm:grid-cols-3 gap-3" data-testid="tv-mode-grid">
          {MODES.map((m) => {
            const Icon = m.icon;
            const active = session.mode === m.key;
            return (
              <button
                key={m.key}
                data-testid={`tv-mode-${m.key}`}
                onClick={() => setMode(m.key)}
                className={`text-left rounded-xl p-4 border transition ${active ? "bg-cyan-400/15 border-cyan-400/50" : "bg-white/[0.02] border-white/8 hover:border-white/20"}`}
              >
                <Icon size={20} className={active ? "text-cyan-300" : "text-white/60"} />
                <div className={`font-display font-bold mt-2 ${active ? "text-cyan-300" : ""}`}>{m.label}</div>
                <div className="text-[11px] text-white/50 mt-0.5">{m.desc}</div>
                {active && <div className="text-[10px] text-cyan-300 mt-2">● ĐANG HOẠT ĐỘNG</div>}
              </button>
            );
          })}
        </div>
      </div>

      {/* QR + URL */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-[#0c0d14] border border-amber-400/30 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold text-sm uppercase tracking-wider text-amber-300">QR Sảnh chờ</div>
            <button onClick={refresh} className="text-xs px-3 py-1.5 rounded bg-amber-400/15 border border-amber-400/40 text-amber-300 flex items-center gap-1.5 hover:bg-amber-400/25" data-testid="tv-refresh-qr">
              <RefreshCw size={12} /> Làm mới
            </button>
          </div>
          <div className="bg-white rounded-xl p-4 grid place-items-center">
            <QRCodeSVG value={checkinUrl} size={180} level="M" />
          </div>
          <div className="mt-3 text-[10px] text-white/40 break-all font-mono">{checkinUrl}</div>
          <button onClick={() => { navigator.clipboard.writeText(checkinUrl); toast.success("Đã copy"); }} className="mt-2 text-[10px] text-cyan-300 hover:underline flex items-center gap-1"><Copy size={10} /> Copy URL</button>
        </div>

        <div className="rounded-2xl bg-[#0c0d14] border border-white/8 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold text-sm uppercase tracking-wider">Người check-in (10 phút)</div>
            <span className="text-xs text-white/50" data-testid="tv-checkin-count">{checkins.length}</span>
          </div>
          {checkins.length === 0 ? (
            <div className="text-white/40 text-sm py-6 text-center">Chưa có ai</div>
          ) : (
            <div className="space-y-2 max-h-[280px] overflow-y-auto" data-testid="tv-checkin-list">
              {checkins.map((c) => (
                <div key={c.id} className="flex items-center gap-3 p-2 rounded bg-white/[0.03] border border-white/5">
                  <img src={c.avatar_url} alt="" className="w-8 h-8 rounded-full" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{c.nickname} {c.title_text && <span className="text-[10px] text-amber-300">· {c.title_text}</span>}</div>
                    <div className="text-[10px] text-white/40">{c.rank} · {c.elo}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl bg-[#0c0d14] border border-white/8 p-5">
        <div className="font-display font-bold text-sm uppercase tracking-wider mb-2">URL TV để mở trên màn hình quán</div>
        <div className="flex items-center gap-2 flex-wrap">
          <code className="text-xs bg-black/40 px-3 py-1.5 rounded border border-white/10">{tvUrl}</code>
          <a href={tvUrl} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded bg-cyan-400/15 border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/25" data-testid="open-tv">Mở TV</a>
        </div>
      </div>
    </div>
  );
}
